import React, { useState } from 'react';
import { components } from 'react-select';
import { Select } from './partials';
import { rem } from 'polished';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  colors,
  animations,
  text,
  fontWeights,
  spaces,
  mixins,
  shadows,
} from '../../../core/styles/theme/default';
import Label from '../Label';
import ErrorMessage from '../ErrorMessage';
import IconSVG from '../../../primitives/IconSVG/IconSVG';

const ValueContainer = (props: any) => {
  const selectedOptionsCount = props
    .getValue()
    .filter((option: any) => option.value !== '*').length;

  return (
    <components.ValueContainer {...props}>
      {props.children}
      {props.selectProps.isMulti && selectedOptionsCount > 0 ? (
        <span className="select__counter">{selectedOptionsCount}</span>
      ) : null}
    </components.ValueContainer>
  );
};

const Option = (props: any) => {
  return (
    <components.Option {...props}>
      {props.isMulti ? (
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
        />
      ) : null}
      <label>{props.label}</label>
    </components.Option>
  );
};

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      {props.isMulti ? (
        <IconSVG iconName="EchIconSettings" />
      ) : (
        <IconSVG iconName="EchIconDown" />
      )}
    </components.DropdownIndicator>
  );
};

const componentsWrapper = { ValueContainer, Option, DropdownIndicator };

const SelectCustomWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const customStyles: any = {
  container: (provided: any) => css`
    ${provided}
    position: relative;

    .react-select__single-value {
      padding-right: ${rem(spaces.theta)};
    }
  `,
  valueContainer: (provided: any, state: any) => {
    const {
      selectProps: { isDisabled },
    } = state;
    return css`
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      max-width: calc(100% - ${rem(40)});
      padding: ${rem(spaces.alpha)} ${rem(spaces.theta)} ${rem(spaces.alpha)}
        ${rem(spaces.gamma)};

      ${isDisabled &&
      css`
        cursor: default;
      `};

      .react-select__multi-value__label {
        text-overflow: ellipsis;
      }

      .react-select__multi-value__remove:hover {
        background-color: ${colors.nightBlue700};
        color: ${colors.white};
      }

      .select__counter {
        position: absolute;
        z-index: 2;
        top: calc(50% - ${rem(spaces.zeta)} / 2 - ${rem(1)});
        right: calc(${rem(spaces.beta)} / 2);
        width: calc(${rem(spaces.zeta)} / 2);
        height: calc(${rem(spaces.zeta)} / 2);
        text-align: center;
        background-color: ${colors.nightBlue700};
        border: 2px solid ${colors.white};
        color: ${colors.white};
        border-radius: 100%;
        ${text.l1};
        line-height: ${rem(spaces.gamma)};
        font-weight: ${fontWeights.medium};
      }
    `;
  },
  control: (provided: any, state: any) => {
    const {
      selectProps: { hasError, isDisabled },
    } = state;
    return css`
      ${provided}
      display: flex;
      flex-wrap: nowrap;
      min-height: ${rem(spaces.theta)};
      background: ${colors.grey200};
      border: solid 1px ${colors.grey300};
      border-radius: ${rem(2)};
      transition: all ${animations.default};

      ${isDisabled &&
      css`
        cursor: default;
      `};

      ${!isDisabled &&
      css`
        &:hover {
          background: ${colors.grey100};
          box-shadow: ${shadows.dropDownHover};
        }
      `};

      ${hasError &&
      css`
        border-color: ${colors.error};
      `};
    `;
  },
  dropdownIndicator: (provided: any, state: any) => {
    const {
      selectProps: { menuIsOpen },
    } = state;
    return css`
      margin-right: ${rem(spaces.gamma)};
      line-height: 1;

      svg {
        transition: transform ease-in-out 0.5s;
      }

      path {
        stroke: ${colors.black};
      }
      ${menuIsOpen &&
      !state.isMulti &&
      css`
        svg {
          transform: rotate(180deg);
        }
      `};
    `;
  },
  indicatorsContainer: () => null,
  placeholder: () => css`
    ${text.p3};
    letter-spacing: ${rem(0.2)};
    color: ${colors.grey900};
  `,
  menu: (provided: any) => css`
    ${provided}
    top: calc(100% - ${rem(spaces.alpha)});
    background: ${colors.white};
    border: 1px solid ${colors.grue300};
    box-shadow: ${shadows.dropDownHover};
    border-radius: ${rem(2)};
    z-index: 4;
  `,
  menuList: (provided: any) => css`
    ${provided}
    width: 100%;
  `,
  option: (provided: any, state: any) => {
    return css`
      ${provided}
      position: relative;
      display: flex;
      width: 100%;
      padding: ${rem(18)} ${rem(spaces.theta)} ${rem(18)} ${rem(spaces.gamma)};
      ${text.p3};
      letter-spacing: ${rem(0.2)};
      color: ${colors.grey700};
      transition: all ${animations.default};

      &:not(:first-of-type) {
        border-top: solid 1px ${colors.grey200};
      }

      &:hover {
        color: ${colors.grey900};
        background: ${colors.grey100};
      }

      label {
        position: relative;
        display: flex;
      }

      ${state.isSelected &&
      css`
        color: ${colors.red};
        background: ${colors.grey100};
      `};

      ${state.isSelected &&
      !state.isMulti &&
      css`
        &::after {
          content: '';
          position: absolute;
          top: calc(50% - ${rem(6)});
          right: ${rem(spaces.delta)};
          width: ${rem(16)};
          height: ${rem(15)};
          background-image: url("data:image/svg+xml,%3Csvg width='16' height='15' viewBox='0 0 16 15' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 1L6.00001 13L1 8.5' stroke='%23111111' stroke-width='2'/%3E%3C/svg%3E%0A");
          background-size: cover;
        }
      `};

      ${state.isMulti &&
      css`
        input {
          ${mixins.visuallyHidden};
        }

        label::before,
        label::after {
          content: '';
          display: block;
        }

        label::before {
          width: ${rem(20)};
          height: ${rem(20)};
          margin-right: ${rem(spaces.gamma)};
          border-radius: ${rem(4)};
          border: solid 1px ${colors.grey600};
          transition: all ${animations.default};
        }

        label::after {
          position: absolute;
          top: ${rem(5)};
          left: ${rem(5)};
          width: ${rem(9.5)};
          height: ${rem(8)};
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 12 11'%3E%3Cpath stroke='%23fff' stroke-width='1.5' d='M10.667 1l-6 8-3.333-3'/%3E%3C/svg%3E");
          background-size: cover;
          transform: scale(0);
          transition: all ${animations.default};
        }
      `};

      ${state.isSelected &&
      state.isMulti &&
      css`
        label::before {
          border-color: ${colors.nightBlue900};
          background-color: ${colors.nightBlue900};
        }

        label::after {
          transform: scale(1);
        }
      `};
    `;
  },
};

const SelectCustom = ({
  className,
  name,
  options,
  selectedValue,
  isMulti,
  isDisabled,
  label,
  showLabel,
  errorText,
  placeholder,
  allowSelectAll,
  onChange,
  selectAllLabel,
  renderSelectedValue = true,
}: SelectCustomProps) => {
  const hasError = !!errorText;
  const ariaProps = {
    'aria-label': label ? label : 'Not searchable',
  };
  const [selectVal, setSelectVal] = useState(null);
  const handleChange: any = (selected: any) => {
    setSelectVal(selected);
    if (typeof onChange === 'function') {
      setTimeout(() => {
        onChange(selected);
      }, 0);
    }
  };

  return (
    <SelectCustomWrapper className={className}>
      {showLabel && <Label text={label} />}
      <Select
        isDisabled={isDisabled}
        isMulti={isMulti}
        isClearable={false}
        classNamePrefix="react-select"
        styles={customStyles}
        options={options}
        defaultValue={
          selectedValue
            ? options.filter((option) =>
                typeof selectedValue === 'string'
                  ? selectedValue === option.value
                  : selectedValue.indexOf(option.value) > -1,
              )
            : ''
        }
        isSearchable={false}
        hasError={hasError}
        placeholder={placeholder}
        components={componentsWrapper}
        closeMenuOnSelect={!isMulti}
        controlShouldRenderValue={renderSelectedValue}
        hideSelectedOptions={false}
        onChange={handleChange}
        allowSelectAll={allowSelectAll}
        name={name}
        value={allowSelectAll ? selectVal : undefined}
        selectAllLabel={selectAllLabel}
        // defaultMenuIsOpen
        {...ariaProps}
      />
      {errorText && <ErrorMessage text={errorText} />}
    </SelectCustomWrapper>
  );
};

export type SelectCustomProps = {
  /** Placeholder of the CustomSelect */
  placeholder?: string;
  /** Class of the CustomSelect */
  className?: string;
  /** name of the CustomSelect */
  name?: string;
  /** Set if CustomSelect is disabled */
  isDisabled?: boolean;
  /** Label Text to be shown */
  label?: any;
  /** Show Label */
  showLabel?: boolean;
  /** Error Text to be shown */
  errorText?: string;
  /** Array of SelectCustom Options */
  options: Array<any>;
  /** SelectCustom onChange */
  onChange?: any;
  /** Value of the CustomSelect */
  value?: string;
  /** Set Multiselect */
  isMulti?: boolean;
  /** preselect option */
  selectedValue?: string | Array<string>;
  allowSelectAll?: boolean;
  selectAllLabel: string;
  renderSelectedValue?: boolean;
};

export default SelectCustom;
